import _useTranslation from 'lib/hooks/_useTranslation'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import FeatureDialog from 'stories/elements/Modal/DialogWindow/FeatureDialog'
import { trackPromise } from 'react-promise-tracker'
import UserService from 'services/UserService'
import { toast } from 'react-toastify'
import { useEffect } from 'react'
import { trackEvent } from 'lib/tracking/analytics-service'
import {
    AnalyticsEventPreventWebappAccessDialogImpression,
    AnalyticsEventPreventWebappAccessDownloadButtonClick,
    AnalyticsEventPreventWebappAccessRefreshFailure,
    AnalyticsEventPreventWebappAccessRefreshSuccess,
} from 'lib/constants/AnalyticsEventType'
import TokenService from 'services/TokenService'
import { ApiError } from 'services/ApiService'
import { emptyFunction } from 'lib/utils'
import { Know } from 'interfaces/Know'
import PreventWebappAccessWhatsappSecurityBody from './PreventWebappAccessWhatsappSecurityBody'
import theme from 'stories/utils/theme'
import { UserPremiumStatus } from 'interfaces/User'
import { useRouter } from 'next/router'
import { getCountryLanguageConfig } from 'lib/constants/countryLanguageConfig'
import { appFeedsRoute, appKnowRoute } from 'lib/constants/routes'
import { ActiveExperiments } from 'lib/features/activeExperiments'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import { AcademicTier } from 'interfaces/University'

const loadingAreas = {
    refresh: 'refresh',
}

interface Props {
    know?: Know
    experiments: ActiveExperiments
}

const PreventWebappAccessDialog = ({ know, experiments }: Props) => {
    const authenticatedUser = useSelector((state: AppState) => state.user)
    const { t: tCommon } = _useTranslation('common')
    const { t } = _useTranslation('app')
    const router = useRouter()
    const lg = useMedia(deviceSize.lg)
    const countryLanguageConfig = getCountryLanguageConfig(router.locale)
    const isLaunched = !!countryLanguageConfig?.isLaunched

    const isUserPremium = authenticatedUser && authenticatedUser?.premiumStatus !== UserPremiumStatus.None

    const userDownloadedMobileApplication =
        !!authenticatedUser?.hasUsedApp || !isLaunched || authenticatedUser?.academicTier === AcademicTier.University

    const isDarkTheme = true

    const refreshUser = () => {
        trackPromise(
            UserService.getMe().then((user) => {
                if (!user?.hasUsedApp) {
                    trackEvent(AnalyticsEventPreventWebappAccessRefreshFailure, { know })
                    toast.info(t('app/preventWebappAccessToastMessage'))
                } else {
                    trackEvent(AnalyticsEventPreventWebappAccessRefreshSuccess, { know })
                    router.push(know ? appKnowRoute(know?.slug, {}) : appFeedsRoute)
                }
            }),
            loadingAreas.refresh
        )
    }

    useEffect(() => {
        if (userDownloadedMobileApplication || isUserPremium) return

        trackEvent(AnalyticsEventPreventWebappAccessDialogImpression, { know })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDownloadedMobileApplication])

    if (userDownloadedMobileApplication || isUserPremium) return null

    const onDownloadButtonClick = (type: 'android' | 'ios') =>
        trackEvent(AnalyticsEventPreventWebappAccessDownloadButtonClick, { value: type, know })

    const handleLogout = () =>
        TokenService.logout().catch((error) => {
            if (error instanceof ApiError) {
                error.handleUnknownError(tCommon, 'auth.logout')
            } else {
                throw error
            }
        })

    if (lg) return null

    return (
        <FeatureDialog
            open={!userDownloadedMobileApplication}
            onClose={emptyFunction}
            ariaPrefix="prevent-webapp-access"
            title=""
            hideCloseButton
            backgroundColor={isDarkTheme ? theme.colors.webappDialogBackground : theme.colors.white}
            disableBackdropClick
            allowVerticalOverflow
            hideTitle
            maxWidth={1050}
        >
            <PreventWebappAccessWhatsappSecurityBody
                onDownloadButtonClick={onDownloadButtonClick}
                handleLogout={handleLogout}
                loadingArea={loadingAreas.refresh}
                refreshUser={refreshUser}
                isDarkTheme={isDarkTheme}
            />
        </FeatureDialog>
    )
}

export default PreventWebappAccessDialog
